import { Injectable, signal, computed, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError, of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';

// Interface pour le plan d'abonnement
interface SubscriptionPlan {
  id: number;
  name: string;
  totalCost: number;
  installmentCount: number;
}
// Interface pour la mise à jour du profil utilisateur
export interface UpdateUserProfileData {
  nom?: string;
  prenom?: string;
  email?: string;
  telephone?: string;
  date?: string;
  lieunaissance?: string;
  adress?: string;
  profil?: string;
  photo?: File;
}

// Interface pour l'abonnement
interface Subscription {
  id: number;
  subscriptionPlan: SubscriptionPlan;
  startDate: number[]; // [year, month, day]
  endDate: number[]; // [year, month, day]
  active: boolean;
  paidAmount: number;
  installmentCount: number;
  dateInvoice: string | null;
  status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  renewed: boolean;
}

// Interface pour les autorités/rôles
interface Authority {
  authority: string;
}

// Enum pour les profils utilisateur
export enum UserProfile {
  ADMIN = 'ADMIN',
  SITE_MANAGER = 'SITE_MANAGER',
  USER = 'USER'
}

export enum profil {
  SITE_MANAGER = 'SITE_MANAGER'
}

// Type pour les profils disponibles
export type ProfileType = keyof typeof profil;

// Interface utilisateur mise à jour avec les nouveaux profils
export interface User {
  id: number;
  nom: string;
  prenom: string;
  email: string;
  password: string;
  date: string,
  lieunaissance: string;
  adress: string;
  technicalSheet: string | null;
  profil: profil[] | string; // ⚠️ CORRECTION: peut être un array ou une string (ex: "PROMOTEUR")
  profils?: string; // ⚠️ AJOUT: propriété pour l'API qui envoie "profils" comme string
  activated: boolean;
  notifiable: boolean;
  telephone: string;
  subscriptions: Subscription[];
  company: any | null;
  createdAt: number[];
  funds: number;
  note: number;
  photo: string | null;
  idCard: string | null;
  dateOfBirth?: string;
  qrcode?: string | null;
  accountNonExpired: boolean;
  credentialsNonExpired: boolean;
  accountNonLocked: boolean;
  authorities: Authority[];
  username: string;
  enabled: boolean;
}

interface LoginResponse {
  token: string;
  refreshToken: string;
  user?: User;
}
interface ResetPassword {
  email: string;
}

// Interface pour la réponse de reset password
interface ResetPasswordResponse {
  message?: string;
  success?: boolean;
  status?: string;
}

// Interface pour les données d'inscription
export interface RegistrationData {
  nom: string;
  prenom: string;
  email: string;
  password: string;
  telephone: string;
  adress: string;
  profil: profil;
  company?: any;
}

// Configuration des profils avec leurs propriétés
export interface ProfileConfig {
  key: profil;
  label: string;
  description: string;
  icon?: string;
  permissions?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private endpoints = environment.endpoints;

  // Configuration des profils disponibles
  private readonly profilesConfig: ProfileConfig[] = [
    {
      key: profil.SITE_MANAGER,
      label: 'Gestionnaire de Site (Promoteur)',
      description: 'Gestion des sites et équipes',
      icon: 'manager-icon',
      permissions: ['SITE_MANAGEMENT', 'TEAM_MANAGEMENT', 'REPORT_CREATE', 'REPORT_VIEW']
    }
  ];

  // Signals pour l'état d'authentification
  private _currentUser = signal<User | null>(null);
  private _isAuthenticated = signal(false);
  private _token = signal<string | null>(null);

  // Signaux en lecture seule pour les composants   
  currentUser = this._currentUser.asReadonly();
  isAuthenticated = this._isAuthenticated.asReadonly();

  // ✅ CORRECTION: Améliorer le computed signal userProfile pour gérer string et array
  userProfile = computed(() => {
    const user = this._currentUser();
    if (!user) return null;

    // Priorité 1: Vérifier la propriété "profils" (string) de l'API
    if (user.profils && typeof user.profils === 'string') {
      return [user.profils as any];
    }

    // Priorité 2: Vérifier la propriété "profil" (array)
    if (Array.isArray(user.profil) && user.profil.length > 0) {
      return user.profil;
    }

    // Priorité 3: Vérifier si "profil" est une string (ex: "PROMOTEUR")
    if (typeof user.profil === 'string') {
      return [user.profil as any];
    }

    return null;
  });

  userFullName = computed(() => {
    const user = this._currentUser();
    return user ? `${user.prenom} ${user.nom}` : null;
  });

  isUserActivated = computed(() => this._currentUser()?.activated || false);
  isUserEnabled = computed(() => this._currentUser()?.enabled || false);

  // Computed signals pour les abonnements
  activeSubscriptions = computed(() =>
    this._currentUser()?.subscriptions.filter(sub => sub.active) || []
  );

  hasActiveSubscription = computed(() => this.activeSubscriptions().length > 0);

  // Computed signals pour les informations de compte
  userFunds = computed(() => this._currentUser()?.funds || 0);
  userNote = computed(() => this._currentUser()?.note || 0);

  isAccountValid = computed(() => {
    const user = this._currentUser();
    return user ?
      user.accountNonExpired &&
      user.credentialsNonExpired &&
      user.accountNonLocked &&
      user.enabled &&
      user.activated : false;
  });
  isADMINProfile(): boolean {
    const user = this.currentUser();
    if (!user) {
      return false;
    }

    console.log("🔍 Vérification profil ADMIN - profil du User connecté:", user.profil);
    console.log("🔍 Vérification profil ADMIN - profils du User connecté:", user.profils);

    // Vérifier d'abord la propriété "profils" (string) de l'API
    if (user.profils && typeof user.profils === 'string') {
      return user.profils === 'ADMIN';
    }

    // Ensuite vérifier la propriété "profil" (array) de l'interface
    if (user.profil && Array.isArray(user.profil)) {
      return user.profil.includes('ADMIN' as any);
    }

    // Vérifier si "profil" est une string (ex: "PROMOTEUR", "ADMIN")
    if (user.profil && typeof user.profil === 'string') {
      return user.profil === 'ADMIN';
    }

    return false;
  }

  isBETProfile(): boolean {
    const user = this.currentUser();
    if (!user) {
      return false;
    }

    // Vérifier d'abord la propriété "profils" (string) de l'API
    if (user.profils && typeof user.profils === 'string') {
      return user.profils === 'BET';
    }

    // Ensuite vérifier la propriété "profil" (array) de l'interface
    if (user.profil && Array.isArray(user.profil)) {
      return user.profil.includes('BET' as any);
    }

    // Vérifier si "profil" est une string
    if (user.profil && typeof user.profil === 'string') {
      return user.profil === 'BET';
    }

    return false;
  }

  isSUPPLIERProfile(): boolean {
    const user = this.currentUser();
    if (!user) {
      return false;
    }

    // Vérifier d'abord la propriété "profils" (string) de l'API
    if (user.profils && typeof user.profils === 'string') {
      return user.profils === 'SUPPLIER';
    }

    // Ensuite vérifier la propriété "profil" (array) de l'interface
    if (user.profil && Array.isArray(user.profil)) {
      return user.profil.includes('SUPPLIER' as any);
    }

    // Vérifier si "profil" est une string
    if (user.profil && typeof user.profil === 'string') {
      return user.profil === 'SUPPLIER';
    }

    return false;
  }

  isPROMOTEURProfile(): boolean {
    const user = this.currentUser();
    if (!user) {
      return false;
    }

    // Vérifier d'abord la propriété "profils" (string) de l'API
    if (user.profils && typeof user.profils === 'string') {
      return user.profils === 'PROMOTEUR';
    }

    // Ensuite vérifier la propriété "profil" (array) de l'interface
    if (user.profil && Array.isArray(user.profil)) {
      return user.profil.includes('PROMOTEUR' as any);
    }

    // Vérifier si "profil" est une string
    if (user.profil && typeof user.profil === 'string') {
      return user.profil === 'PROMOTEUR';
    }

    return false;
  }

  // ✅ CORRECTION: Computed signal pour les permissions basées sur les profils (array)
  userPermissions = computed(() => {
    const profiles = this.userProfile();
    if (!profiles || profiles.length === 0) return [];

    const allPermissions: string[] = [];
    profiles.forEach(profile => {
      const config = this.profilesConfig.find(p => p.key === profile);
      if (config?.permissions) {
        allPermissions.push(...config.permissions);
      }
    });

    // Retourner les permissions uniques
    return [...new Set(allPermissions)];
  });
  userState$: any;

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.initializeAuthState();
  }
  updateUserWithFormData(userId: number, formData: FormData): Observable<User> {
    console.log('🔄 Mise à jour de l\'utilisateur ID:', userId, 'avec FormData');

    // Log pour debug
    console.log('📋 FormData contient:');
    formData.forEach((value, key) => {
      if (value instanceof File) {
        console.log(`  - ${key}: [File] ${value.name} (${value.size} bytes)`);
      } else {
        console.log(`  - ${key}: ${value}`);
      }
    });

    // Headers avec uniquement Authorization
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.getToken()}`
    });

    const updateUrl = `${this.endpoints.user.update.replace('{id}', userId.toString())}`;
    console.log('🌐 Endpoint:', updateUrl);

    return this.http.put<User>(updateUrl, formData, { headers }).pipe(
      tap(updatedUser => {
        console.log('✅ Utilisateur mis à jour avec succès:', updatedUser);

        // Mettre à jour l'utilisateur dans le state
        this._currentUser.set(updatedUser);

        // Mettre à jour aussi dans localStorage
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('user', JSON.stringify(updatedUser));
          console.log('💾 Utilisateur mis à jour dans localStorage');
        }
      }),
      catchError(error => {
        console.error('❌ Erreur lors de la mise à jour:', error);
        throw error;
      })
    );
  }
  // Nouvelle méthode pour mettre à jour UN utilisateur (pas le currentUser)
  updateAnyUserWithFormData(userId: number, formData: FormData): Observable<User> {
    console.log('🔄 Mise à jour de l\'utilisateur ID:', userId, 'avec FormData');

    // Log pour debug
    console.log('📋 FormData contient:');
    formData.forEach((value, key) => {
      if (value instanceof File) {
        console.log(`  - ${key}: [File] ${value.name} (${value.size} bytes)`);
      } else {
        console.log(`  - ${key}: ${value}`);
      }
    });

    // Headers avec uniquement Authorization
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.getToken()}`
    });

    const updateUrl = `${this.endpoints.user.update.replace('{id}', userId.toString())}`;
    console.log('🌐 Endpoint:', updateUrl);

    return this.http.put<User>(updateUrl, formData, { headers }).pipe(
      tap(updatedUser => {
        console.log('✅ Utilisateur mis à jour avec succès:', updatedUser);

        // ✅ IMPORTANT: Ne PAS mettre à jour le currentUser
        // On met à jour le currentUser UNIQUEMENT si c'est l'utilisateur connecté
        const currentUserId = this._currentUser()?.id;
        if (currentUserId === userId) {
          this._currentUser.set(updatedUser);
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('user', JSON.stringify(updatedUser));
            console.log('💾 Utilisateur connecté mis à jour dans localStorage');
          }
        }
      }),
      catchError(error => {
        console.error('❌ Erreur lors de la mise à jour:', error);
        throw error;
      })
    );
  }

  private initializeAuthState(): void {
    console.log('🚀 Initialisation de l\'état d\'authentification');
    if (isPlatformBrowser(this.platformId)) {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      console.log('📦 Contenu localStorage:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        tokenLength: storedToken?.length,
        userLength: storedUser?.length
      });

      // ✅ PERMETTRE LA PERSISTANCE: Restaurer la session même si le token est expiré
      if (storedToken && storedUser) {
        console.log('✅ Token et utilisateur trouvés dans localStorage');
        this._token.set(storedToken);
        this._isAuthenticated.set(true);

        try {
          const user = JSON.parse(storedUser);
          this._currentUser.set(user);
          console.log('✅ Utilisateur restauré depuis localStorage:', {
            id: user.id,
            email: user.email,
            profils: user.profil
          });
        } catch (error) {
          console.error('❌ Erreur parsing utilisateur depuis localStorage:', error);
          // Si erreur de parsing, nettoyer les données
          this.clearAuthData();
          return;
        }

        // Vérifier la validité du token mais ne pas déconnecter si expiré
        const tokenValid = this.isTokenValid();
        console.log('🔍 Validité du token:', tokenValid);

        if (tokenValid) {
          // Rafraîchir l'utilisateur depuis l'API en arrière-plan
          this.getCurrentUser().subscribe({
            next: (user) => {
              this._currentUser.set(user);
              console.log('✅ Utilisateur rafraîchi depuis API:', user);
            },
            error: (error) => {
              console.warn('⚠️ Erreur rafraîchissement utilisateur (continu avec localStorage):', error);
              // Ne pas nettoyer les données, continuer avec localStorage
            }
          });
        } else {
          console.warn('⚠️ Token expiré, mais on garde la session avec localStorage pour la persistance');
          // ✅ IMPORTANT: NE PAS nettoyer les données, permettre à l'utilisateur de continuer
          // Le token sera rafraîchi lors de la prochaine requête API
        }
      } else {
        console.log('❌ Pas de token ou d\'utilisateur dans localStorage');
      }
    } else {
      console.log('⚠️ Pas en mode browser (SSR)');
    }
  }

  // ✅ NOUVELLE MÉTHODE: Vérification du format du token
  private isTokenValidFormat(token: string): boolean {
    try {
      const parts = token.split('.');
      return parts.length === 3;
    } catch {
      return false;
    }
  }

  // Méthodes pour obtenir les profils
  getAvailableProfiles(): ProfileConfig[] {
    return this.profilesConfig;
  }

  getProfileConfig(profile: profil): ProfileConfig | undefined {
    return this.profilesConfig.find(p => p.key === profile);
  }

  getRegistrationProfiles(): ProfileConfig[] {
    return this.profilesConfig; // Tous les profils sont disponibles pour l'inscription
  }

  register(registrationData: RegistrationData): Observable<any> {
    if (!Object.values(profil).includes(registrationData.profil)) {
      throw new Error('Profil utilisateur invalide');
    }

    const formData = new FormData();
    Object.entries(registrationData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    });

    return this.http.post(`${this.endpoints.auth.signup}`, formData);
  }

  registerWithFormData(data: FormData): Observable<any> {
    return this.http.post(`${this.endpoints.auth.signup}`, data);
  }

  login(credentials: { email: string; password: string }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.endpoints.auth.signin}`, credentials)
      .pipe(
        tap(response => {
          if (response.user) {
            this.setAuthData(response.token, response.user);
          } else {
            // Si pas d'utilisateur, stocker juste le token
            try {
              localStorage.setItem('token', response.token);
              if (response.refreshToken) {
                localStorage.setItem('refreshToken', response.refreshToken);
              }
              this._token.set(response.token);
              this._isAuthenticated.set(true);
            } catch (error) {
              console.error('❌ Erreur stockage token:', error);
            }
          }
        })
      );
  }

  /**
   * Connexion via l'API de pointage
   * @param credentials Email et mot de passe
   * @returns Observable de la réponse de connexion
   */
  loginWithPointageApi(credentials: { email: string; password: string }): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.endpoints.auth.signin}`, credentials)
      .pipe(
        tap(response => {
          console.log('=== LOGIN RESPONSE DEBUG ===');
          console.log('Token reçu:', !!response.token);
          console.log('RefreshToken reçu:', !!response.refreshToken);
          console.log('User reçu:', response.user);
          console.log('========================');

          // ✅ STOCKER LE TOKEN DIRECTEMENT DANS LOCALSTORAGE
          try {
            localStorage.setItem('token', response.token);
            localStorage.setItem('refreshToken', response.refreshToken);
            console.log('✅ Token et refreshToken stockés dans localStorage');

            // Mettre à jour le signal du token
            this._token.set(response.token);
            this._isAuthenticated.set(true);
            console.log('✅ Signal token et isAuthenticated mis à jour');
          } catch (error) {
            console.error('❌ Erreur stockage token dans localStorage:', error);
          }

          // ✅ RÉCUPÉRER L'UTILISATEUR APRÈS LA CONNEXION
          this.getCurrentUser().subscribe({
            next: (user) => {
              console.log('✅ Utilisateur récupéré après connexion:', user);
              this.setAuthData(response.token, user);
            },
            error: (error) => {
              console.error('❌ Erreur récupération utilisateur après connexion:', error);
              // Stocker quand même le token même si l'utilisateur n'est pas récupéré
            }
          });
        })
      );
  }

  /**
   * Réinitialise le mot de passe de l'utilisateur en envoyant un email
   * @param credentials Contient l'email de l'utilisateur
   * @returns Observable de la réponse de l'API
   */
  resetPassword(credentials: { email: string }): Observable<any> {
    console.log('🔄 Envoi de la demande de réinitialisation');
    console.log('📧 Email:', credentials.email);
    console.log('📍 URL:', `${this.endpoints.auth.resetPassword}`);
    console.log('📦 Body:', JSON.stringify(credentials));

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(
      `${this.endpoints.auth.resetPassword}`,
      credentials,
      { headers }
    ).pipe(
      tap(response => {
        console.log('✅ Réponse reset password reçue:', response);
        // Pour le reset password, on ne stocke pas de token
        // C'est juste une confirmation d'envoi d'email
      }),
      catchError(error => {
        console.error('❌ Erreur reset password - Status:', error.status);
        console.error('❌ Erreur reset password - Message:', error.message);
        console.error('❌ Erreur reset password - Body:', error.error);
        console.error('❌ Erreur reset password - Complète:', error);
        throw error;
      })
    );
  }


  logout(): void {
    this.clearAuthData();
  }

  private setAuthData(token: string, user: User): void {
    console.log('=== SET AUTH DATA DEBUG ===');
    console.log('Platform ID:', this.platformId);
    console.log('Is Platform Browser:', isPlatformBrowser(this.platformId));
    console.log('Token présent:', !!token);
    console.log('User présent:', !!user);
    console.log('User email:', user?.email);
    console.log('========================');

    // ✅ FORCER LE STOCKAGE: Stocker dans localStorage sans vérifier isPlatformBrowser
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      console.log('✅ Token et utilisateur stockés dans localStorage (forcé)');

      // Vérifier le stockage
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      console.log('📦 Vérification localStorage après stockage:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
        tokenLength: storedToken?.length,
        userLength: storedUser?.length
      });
    } catch (error) {
      console.error('❌ Erreur lors du stockage dans localStorage:', error);
    }

    this._token.set(token);
    this._currentUser.set(user);
    this._isAuthenticated.set(true);

    console.log('✅ État d\'authentification mis à jour:', {
      token: !!token,
      user: user?.email,
      profils: user?.profil
    });
  }

  private clearAuthData(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }

    this._token.set(null);
    this._currentUser.set(null);
    this._isAuthenticated.set(false);

    console.log('🧹 Données d\'authentification nettoyées');
  }

  // ✅ CORRECTION MAJEURE: Méthode pour obtenir le token
  getToken(): string | null {
    const token = this._token();

    if (!token) {
      console.warn('⚠️ Aucun token dans le signal');
      return null;
    }

    // ✅ PERMETTRE LA PERSISTANCE: Ne pas nettoyer les données si le token est expiré
    if (!this.isTokenValid()) {
      console.warn('⚠️ Token expiré, mais on garde les données pour la persistance');
      // Ne pas nettoyer les données, permettre à l'utilisateur de continuer
    }

    return token;
  }

  // ✅ CORRECTION MAJEURE: Méthode pour obtenir les headers d'authentification
  getAuthHeaders(): HttpHeaders {
    const token = this.getToken();

    console.log('=== AUTH HEADERS DEBUG ===');
    console.log('Token récupéré:', token ? `${token.substring(0, 20)}...` : 'null');

    if (!token) {
      console.error('❌ Aucun token valide disponible');
      return new HttpHeaders({
        'Content-Type': 'application/json'
      });
    }

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    console.log('✅ Headers créés avec Authorization');
    console.log('========================');

    return headers;
  }

  // ✅ AMÉLIORATION: Validation du token plus robuste
  isTokenValid(): boolean {
    const token = this._token();

    if (!token) {
      return false;
    }

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('❌ Format de token invalide');
        return false;
      }

      const payload = JSON.parse(atob(tokenParts[1]));

      // Vérifier l'expiration avec une marge de 5 minutes
      if (payload.exp) {
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        const marginTime = 5 * 60 * 1000; // 5 minutes de marge

        if (expirationTime - marginTime <= currentTime) {
          console.warn('⚠️ Token proche de l\'expiration ou expiré');
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur validation token:', error);
      return false;
    }
  }

  // ✅ MÉTHODE CORRIGÉE: Récupération de l'utilisateur avec meilleure gestion d'erreurs
  getCurrentUser(): Observable<User> {
    const headers = this.getAuthHeaders();

    // Vérifier si on a un token valide avant de faire l'appel
    if (!headers.get('Authorization')) {
      console.error('❌ Impossible de récupérer l\'utilisateur: pas d\'autorisation');
      return of(null as any);
    }

    return this.http.get<User>(`${this.endpoints.user.me}`, { headers }).pipe(
      tap(user => {
        if (user) {
          this._currentUser.set(user);
          console.log('✅ Utilisateur mis à jour:', {
            id: user.id,
            email: user.email,
            profils: user.profil,
            activated: user.activated,
            enabled: user.enabled
          });
        }
      }),
      catchError(error => {
        console.error('❌ Erreur récupération utilisateur:', error);
        if (error.status === 401 || error.status === 403) {
          console.log('🧹 Token invalide, nettoyage automatique');
          this.clearAuthData();
        }
        return of(null as any);
      })
    );
  }

  // ✅ NOUVELLES MÉTHODES: Vérifications des profils avec gestion d'array
  hasProfile(profile: profil): boolean {
    const userProfiles = this.userProfile();
    return userProfiles ? userProfiles.includes(profile) : false;
  }

  hasAnyProfile(profiles: profil[]): boolean {
    const userProfiles = this.userProfile();
    if (!userProfiles) return false;

    return profiles.some(profile => userProfiles.includes(profile));
  }

  isSiteManager(): boolean {
    return this.hasProfile(profil.SITE_MANAGER);
  }

  // ✅ MÉTHODES CORRIGÉES: Vérifications des permissions
  hasAuthority(authority: string): boolean {
    const user = this._currentUser();
    return user?.authorities.some(auth => auth.authority === authority) || false;
  }

  hasPermission(permission: string): boolean {
    const permissions = this.userPermissions();
    return permissions.includes(permission);
  }

  hasAnyPermission(requiredPermissions: string[]): boolean {
    const userPermissions = this.userPermissions();
    return requiredPermissions.some(permission => userPermissions.includes(permission));
  }

  // ✅ NOUVELLES MÉTHODES: Vérifications des actions spécifiques
  canCreateReport(): boolean {
    return this.isAccountValid() &&
      this.isAuthenticated() &&
      this.hasPermission('REPORT_CREATE');
  }

  canViewReport(): boolean {
    return this.isAccountValid() &&
      this.isAuthenticated() &&
      this.hasPermission('REPORT_VIEW');
  }

  canPerformAction(): boolean {
    return this.isAccountValid() && this.isAuthenticated();
  }

  // Méthodes spécifiques aux nouveaux profils
  canManageSites(): boolean {
    return this.isSiteManager() && this.hasPermission('SITE_MANAGEMENT');
  }

  canAccessProjects(): boolean {
    return this.canManageSites();
  }

  // Méthodes utilitaires pour les abonnements
  getActiveSubscription(): Subscription | null {
    const activeSubscriptions = this.activeSubscriptions();
    return activeSubscriptions.length > 0 ? activeSubscriptions[0] : null;
  }

  getSubscriptionStatus(): string {
    const activeSubscription = this.getActiveSubscription();
    return activeSubscription?.status || 'NONE';
  }

  // ✅ NOUVELLE MÉTHODE: ID de l'utilisateur connecté
  getConnectedUserId(): number | null {
    return this._currentUser()?.id ?? null;
  }

  // Méthode pour formater la date de création
  getUserCreationDate(): Date | null {
    const user = this._currentUser();
    if (!user?.createdAt) return null;

    const [year, month, day, hour, minute, second] = user.createdAt;
    return new Date(year, month - 1, day, hour, minute, second);
  }

  // Méthode pour formater les dates d'abonnement
  formatSubscriptionDate(dateArray: number[]): Date {
    const [year, month, day] = dateArray;
    return new Date(year, month - 1, day);
  }

  // Méthode pour rafraîchir les informations utilisateur
  refreshUser(): Observable<User> {
    return this.getCurrentUser();
  }

  // Méthode pour mettre à jour le profil utilisateur (avec ou sans photo)
  updateUserProfile(formData: FormData, id: number): Observable<User> {
    // Pour FormData, ne pas définir Content-Type (le navigateur le fait automatiquement avec boundary)
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.getToken()}`
    });

    console.log('🔄 Mise à jour du profil utilisateur ID:', id);
    console.log('📦 FormData contient:', Array.from((formData as any).entries()));

    return this.http.put<User>(`${this.endpoints.user.byId.replace('{id}', id.toString())}`, formData, { headers }).pipe(
      tap(updatedUser => {
        // Mettre à jour l'utilisateur dans le state
        this._currentUser.set(updatedUser);
        console.log('✅ Profil utilisateur mis à jour:', updatedUser);
      }),
      catchError(error => {
        console.error('❌ Erreur mise à jour profil:', error);
        throw error;
      })
    );
  }

  // Méthodes utilitaires pour les informations utilisateur
  getUserInitials(): string {
    const user = this._currentUser();
    if (!user) return '';

    const firstInitial = user.prenom.charAt(0).toUpperCase();
    const lastInitial = user.nom.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  getUserDisplayName(): string {
    return this.userFullName() || 'Utilisateur';
  }

  /**
   * Retourne l'URL complète de la photo de l'utilisateur
   * @param userPhotoPath La photo path de l'utilisateur (optionnel, utilise l'utilisateur courant si non fourni)
   */
  getUserPhotoUrl(userPhotoPath?: string): string {
    // ✅ Si un chemin est fourni en paramètre, l'utiliser
    if (userPhotoPath) {
      return this.buildPhotoUrl(userPhotoPath);
    }

    // ✅ Sinon, utiliser la photo de l'utilisateur courant
    const user = this._currentUser();
    if (user?.photo) {
      return this.buildPhotoUrl(user.photo);
    }

    // ✅ NE PAS UTILISER L'ENDPOINT PHOTO QUI N'EXISTE PAS
    // L'endpoint /api/v1/user/{id}/photo retourne une erreur 500
    // Retourner le placeholder par défaut
    return '/avatar-placeholder.svg';
  }

  /**
   * Construit l'URL de la photo à partir d'un chemin
   * @param photoPath Le chemin de la photo
   */
  private buildPhotoUrl(photoPath: string): string {
    if (!photoPath) {
      return '/avatar-placeholder.svg';
    }

    // ✅ Si c'est une URL complète (http ou https)
    if (photoPath.startsWith('http://') || photoPath.startsWith('https://')) {
      return photoPath;
    }

    // ✅ Si c'est un chemin absolu (commence par /)
    if (photoPath.startsWith('/')) {
      return photoPath;
    }

    // ✅ Sinon, c'est un chemin relatif, le préfixer par /
    return `/${photoPath}`;
  }

  hasUserPhoto(): boolean {
    const user = this._currentUser();
    return user?.photo !== null && user?.photo !== undefined;
  }

  // Méthodes pour la gestion des notifications
  canReceiveNotifications(): boolean {
    return this._currentUser()?.notifiable || false;
  }

  // Méthodes pour vérifier la validité du compte
  isAccountExpired(): boolean {
    const user = this._currentUser();
    return user ? !user.accountNonExpired : true;
  }

  areCredentialsExpired(): boolean {
    const user = this._currentUser();
    return user ? !user.credentialsNonExpired : true;
  }

  isAccountLocked(): boolean {
    const user = this._currentUser();
    return user ? !user.accountNonLocked : true;
  }

  // ✅ NOUVELLES MÉTHODES: Formatage des profils
  getFormattedUserProfile(): string[] {
    const profiles = this.userProfile();
    if (!profiles) return ['Utilisateur'];

    return profiles.map(profile => {
      const config = this.getProfileConfig(profile);
      return config?.label || profile;
    });
  }

  getUserProfileDescription(): string[] {
    const profiles = this.userProfile();
    if (!profiles) return ['Accès de base au système'];

    return profiles.map(profile => {
      const config = this.getProfileConfig(profile);
      return config?.description || 'Accès de base au système';
    });
  }

  // Méthode pour valider les données d'inscription
  validateRegistrationData(data: RegistrationData): string[] {
    const errors: string[] = [];

    if (!data.nom?.trim()) {
      errors.push('Le nom est requis');
    }

    if (!data.prenom?.trim()) {
      errors.push('Le prénom est requis');
    }

    if (!data.email?.trim()) {
      errors.push('L\'email est requis');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errors.push('Format d\'email invalide');
    }

    if (!data.password?.trim()) {
      errors.push('Le mot de passe est requis');
    } else if (data.password.length < 6) {
      errors.push('Le mot de passe doit contenir au moins 6 caractères');
    }

    if (!data.telephone?.trim()) {
      errors.push('Le téléphone est requis');
    }

    if (!data.profil) {
      errors.push('Le profil est requis');
    } else if (!Object.values(profil).includes(data.profil)) {
      errors.push('Profil utilisateur invalide');
    }

    return errors;
  }
  // ✅ NOUVELLE MÉTHODE: Récupérer le premier profil de manière fiable
  getUserFirstProfile(): string | null {
    const user = this._currentUser();
    if (!user) return null;

    // Vérifier la propriété profils (string) en premier
    if (user.profils && typeof user.profils === 'string') {
      return user.profils;
    }

    // Ensuite vérifier profil (array)
    if (Array.isArray(user.profil) && user.profil.length > 0) {
      return user.profil[0];
    }

    // Fallback: profil comme string
    if (typeof user.profil === 'string') {
      return user.profil as any;
    }

    return null;
  }

  // ✅ MÉTHODE DE DEBUG
  debugAuthState(): void {
    console.log('=== DEBUG AUTH STATE ===');
    console.log('Token:', this._token() ? 'Present' : 'Absent');
    console.log('Token valide:', this.isTokenValid());
    console.log('Authentifié:', this.isAuthenticated());
    console.log('Utilisateur:', this._currentUser());
    console.log('Profils:', this.userProfile());
    console.log('Permissions:', this.userPermissions());
    console.log('Compte valide:', this.isAccountValid());
    console.log('=======================');
  }
}